import type { Point } from "@labeling-tools/ui-components/src/ImageWithBoundingBox";
import {
  isAxisAlignedQuad,
  orderCorners,
} from "@labeling-tools/ui-components/src/utils/obbGeometry";
import { getBoundingBoxStyle } from "./utils";

export interface DetectionBoxGeometry {
  /** Absolute-position style for the wrapper div — the detection's AABB. */
  style: ReturnType<typeof getBoundingBoxStyle>;
  /**
   * The corners to stroke, in the wrapper div's local 0-100 space, ready for an
   * SVG polygon. An oriented (rotated) detection gets its true quad; anything
   * else — axis-aligned, degenerate, not a quad — gets the box's own corners.
   */
  corners: Point[];
}

/** The box div's own corners — the detection's AABB — as a polygon ring. */
export const AABB_CORNERS: Point[] = [
  [0, 0],
  [100, 0],
  [100, 100],
  [0, 100],
];

/**
 * Split a detection's normalized points into the AABB the box div is
 * positioned with, plus the corners the overlay strokes on it.
 *
 * Mirrors how `ImageWithBoundingBox` renders OBBs in the confirmation flow:
 * the div stays the hit area and content anchor, the polygon carries the
 * shape. See `obbGeometry` for the corner-ordering rules.
 */
export const getDetectionBoxGeometry = (
  points: number[][] = [],
): DetectionBoxGeometry => {
  const style = getBoundingBoxStyle(points);

  // isAxisAlignedQuad also returns true for anything that isn't 4 points, so
  // irregular inputs fall back to the AABB ring.
  if (isAxisAlignedQuad(points as Point[]))
    return { style, corners: AABB_CORNERS };

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const width = maxX - minX;
  const height = maxY - minY;
  // A zero-width/height AABB can't host the polygon (division by zero), and
  // there is nothing to see anyway.
  if (width <= 0 || height <= 0) return { style, corners: AABB_CORNERS };

  return {
    style,
    corners: orderCorners(points as Point[]).map(
      ([x, y]): Point => [
        ((x - minX) / width) * 100,
        ((y - minY) / height) * 100,
      ],
    ),
  };
};
